import { useEffect, useState, type KeyboardEvent } from "react";
import { LIMITS, type ListKind } from "@checklist/shared";
import { fieldCls, labelCls } from "../AuthShell.js";
import { Button } from "../ui/Button.js";
import { Modal } from "../ui/Modal.js";

function kindToggleCls(active: boolean): string {
  return [
    "h-9 rounded-lg border px-3 text-sm font-semibold transition",
    active
      ? "border-brand-600 bg-brand-600 text-white"
      : "border-line bg-white text-ink hover:bg-slate-50",
  ].join(" ");
}

export function SaveSelectionAsListModal({
  open,
  onClose,
  itemTexts,
  defaultKind,
  busy,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  itemTexts: readonly string[];
  defaultKind: ListKind;
  busy: boolean;
  onSave: (input: { name: string; kind: ListKind; removeFromChecklist: boolean }) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<ListKind>(defaultKind);
  const [removeFromChecklist, setRemoveFromChecklist] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName("");
    setKind(defaultKind);
    setRemoveFromChecklist(false);
  }, [open, defaultKind]);

  const overLimit = itemTexts.length > LIMITS.listItemsMax;
  const canSave = !busy && !overLimit && name.trim().length > 0;

  function submit() {
    if (!canSave) return;
    void onSave({ name: name.trim().replace(/\s+/g, " "), kind, removeFromChecklist });
  }

  function handleNameKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  }

  return (
    <Modal open={open} onClose={onClose} labelledBy="saveSelectionAsListTitle">
      <h2 id="saveSelectionAsListTitle" className="text-lg font-semibold text-ink">
        Save as list
      </h2>

      <label htmlFor="saveSelectionListName" className={`${labelCls} mt-4`}>
        Name
      </label>
      <input
        id="saveSelectionListName"
        autoFocus
        className={fieldCls}
        value={name}
        maxLength={LIMITS.nameMax}
        disabled={busy}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={handleNameKeyDown}
      />

      <div className="mt-4">
        <p className={labelCls} id="saveSelectionKindLabel">
          Kind
        </p>
        <div
          className="mt-1 flex flex-wrap gap-2"
          role="group"
          aria-labelledby="saveSelectionKindLabel"
        >
          <button
            type="button"
            className={kindToggleCls(kind === "packing")}
            aria-pressed={kind === "packing"}
            disabled={busy}
            onClick={() => setKind("packing")}
          >
            Packing
          </button>
          <button
            type="button"
            className={kindToggleCls(kind === "todo")}
            aria-pressed={kind === "todo"}
            disabled={busy}
            onClick={() => setKind("todo")}
          >
            To-do
          </button>
        </div>
      </div>

      <label className="mt-4 flex items-center gap-2 text-sm text-ink">
        <input
          type="checkbox"
          className="size-4 accent-brand-600"
          checked={removeFromChecklist}
          disabled={busy}
          onChange={(e) => setRemoveFromChecklist(e.target.checked)}
        />
        Also remove these from this checklist
      </label>

      <p className="mt-3 text-xs text-muted-sm">Items are saved unchecked, as a flat list.</p>

      {overLimit ? (
        <p className="mt-3 text-sm text-danger" role="alert">
          Lists can have at most {LIMITS.listItemsMax} items — this selection has {itemTexts.length}.
        </p>
      ) : null}

      <div className="mt-5 flex gap-2">
        <Button type="button" variant="secondary" fullWidth disabled={busy} onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" fullWidth disabled={!canSave} onClick={submit}>
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </Modal>
  );
}
